/**
 * Какие разделы кабинета открыты на домене продукта.
 *
 * Кабинет один на всю платформу: те же файлы, те же настройки, та же
 * учётная запись. Второй кабинет означал бы вторую очередь правок и
 * расхождение поведения там, где человек ждёт одинакового.
 *
 * Но открыт он не целиком. На DataHunter продукт пока про хранение и
 * инструменты, а лента дел и ветки относятся к INGDG. Показывать их
 * значило бы обещать то, чего на этом домене нет.
 *
 * Разделы не прячутся серыми, а отсутствуют: закрытый пункт в меню это
 * обещание, которое интерфейс не выполнит.
 */

import { productByHost } from '@/lib/products';

/**
 * Разделы кабинета, доступные на домене DataHunter.
 *
 * «Баланс» открыт на всех доменах: кошелёк принадлежит человеку, а не
 * продукту (#68, lamver/AI-Tech-API#124), в отличие от файлов и настроек, которые здесь
 * же — те открыты потому, что нужны конкретно этому продукту.
 */
const DATAHUNTER_SECTIONS = ['/dashboard/files', '/dashboard/wallet', '/dashboard/settings'];

/** Куда вести с корня кабинета, если главная на этом домене закрыта. */
export const DATAHUNTER_HOME = '/dashboard/files';

/**
 * Разделы кабинета, доступные на домене AiSearch.
 *
 * Главная здесь открыта, в отличие от DataHunter: на ней стоит тот же
 * верстак рецептов, что на витрине, и человеку после входа надо
 * оказаться там, где он работал до входа, а не в списке файлов.
 *
 * До этого главная была закрыта, и корень кабинета вёл на файлы. В
 * меню при этом оставался пункт «Главная» без подписи и без ссылки:
 * пустая строка в списке разделов.
 *
 * «Рабочие пространства» открывались по слову владельца (28.09.2026), а
 * 26.10.2026 он же попросил их пока скрыть: раздел не доведён до продажи.
 * Вернуть — одна строка `'/dashboard/spaces'` в списке ниже. Артефакты,
 * ветки и workflow остаются закрытыми.
 */
const AISEARCH_SECTIONS = [
  ...DATAHUNTER_SECTIONS,
  '/dashboard/tarify',
  // Шаг «Продолжить покупку» (lib/vybor-pokupki.ts). Пункта меню у него
  // нет: сюда приходят с кнопки тарифа, после входа и из плашки.
  '/dashboard/pokupka',
  // «Партнёрам» (рефералка). Пункт меню появляется, только когда
  // программа включена в API; сама страница при выключенной отвечает
  // «не найдено» (app/[locale]/dashboard/partneram/page.tsx).
  '/dashboard/partneram',
];

/**
 * Пути, открытые сами по себе, без вложенного.
 *
 * Главная кабинета это именно такой путь. Попади она в обычный список,
 * и открылся бы весь кабинет целиком: разрешение действует на раздел
 * вместе с вложенным, а вложено в `/dashboard` вообще всё. Так на
 * AiSearch в меню и появились «Рабочие пространства» с «Артефактами» —
 * разделы, которых на этом домене нет.
 */
const AISEARCH_EXACT = ['/dashboard'];

/** Куда вести с корня кабинета на домене AiSearch. */
export const AISEARCH_HOME = '/dashboard';

/** Набор разделов кабинета для продукта. */
export interface AccountAccess {
  /** Идентификатор продукта: по нему меню решает, что показывать. */
  productId: string;
  /** Открыт ли кабинет целиком. */
  full: boolean;
  /** Разрешённые пути, если кабинет открыт частично. */
  allowed: string[];
  /** Пути, открытые без вложенного: разрешают себя и только себя. */
  exact?: string[];
  /** Куда вести с корня кабинета. */
  home: string;
}

/** Права на разделы кабинета по хосту запроса. */
export function accountAccess(host: string | null | undefined): AccountAccess {
  const product = productByHost(host);

  if (product.id === 'datahunter') {
    return {
      productId: product.id,
      full: false,
      allowed: DATAHUNTER_SECTIONS,
      home: DATAHUNTER_HOME,
    };
  }

  if (product.id === 'aisearch') {
    return {
      productId: product.id,
      full: false,
      allowed: AISEARCH_SECTIONS,
      exact: AISEARCH_EXACT,
      home: AISEARCH_HOME,
    };
  }

  return { productId: product.id, full: true, allowed: [], home: '/dashboard' };
}

/**
 * Открыт ли этот путь кабинета на домене.
 *
 * Путь без префикса локали: `/dashboard/files`, а не `/ru/dashboard/files`.
 */
export function isSectionAllowed(access: AccountAccess, bare: string): boolean {
  if (access.full) return true;
  if (access.exact?.includes(bare)) return true;
  return access.allowed.some((path) => bare === path || bare.startsWith(`${path}/`));
}
